import { createContext, ReactNode, useEffect, useState } from 'react';
import { EVENTS_THEME_BACKGROUND_COLOR, EVENTS_THEME_TEXT_COLOR } from '../constants';

const initialSettings = {
    showDescription: false,
    description: '',
    smallImage: '',
    mediumImage: '',
    largeImage: '',
    showAcknowledgments: false,
    acknowledgements: [] as string[],
    hideAfterRegistrationClose: false,
    bannerImage: '',
    backgroundColor: EVENTS_THEME_BACKGROUND_COLOR,
    textColor: EVENTS_THEME_TEXT_COLOR,
    // Custom hero background (type 14 from client settings API)
    heroBackground: '',
    // Client setting type=20: event waivers toggle.
    showEventWaivers: false,
};

type SettingsContextType = {
    settings: typeof initialSettings;
    loading: boolean;
    saveSettings: (data: any) => void;
};

export const SettingsContext = createContext<SettingsContextType>({
    settings: initialSettings,
    loading: true,
    saveSettings: () => {}
});

type SettingsProviderProps = {
    children: ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
    const [settings, setSettings] = useState<typeof initialSettings>(initialSettings);
    const [loading, setLoading] = useState(true);

    const saveSettings = (newSettings: any) => {
        const easyRegistration = newSettings.find((setting: any) => setting.type === 1);
        const description = newSettings.find((setting: any) => setting.type === 2);
        const smallImage = newSettings.find((setting: any) => setting.type === 4);
        const mediumImage = newSettings.find((setting: any) => setting.type === 5);
        const largeImage = newSettings.find((setting: any) => setting.type === 6);
        const acknowledgements = newSettings.find((setting: any) => setting.type === 8);
        const hideAfterRegistrationClose = newSettings.find((setting: any) => setting.type === 7);
        const bannerImage = newSettings.find((setting: any) => setting.type === 9);
        const heroBackground = newSettings.find((setting: any) => setting.type === 14);
        const eventWaivers = newSettings.find((setting: any) => setting.type === 20);

        setSettings((prevSettings) => ({
            ...prevSettings,
            ...{
                easyRegistration: easyRegistration?.status === 1,
                showDescription: description?.status === 1,
                description: description?.status === 1 ? description?.data : '',
                smallImage: smallImage?.status === 1 ? smallImage?.data : '',
                mediumImage: mediumImage?.status === 1 ? mediumImage?.data : '',
                largeImage: largeImage?.status === 1 ? largeImage?.data : '',
                showAcknowledgments: acknowledgements?.status === 1,
                acknowledgements: acknowledgements?.status === 1 ? JSON.parse(acknowledgements.data) : '',
                hideAfterRegistrationClose: hideAfterRegistrationClose?.status === 1,
                bannerImage: bannerImage?.status === 1 ? bannerImage.data : '',
                backgroundColor: EVENTS_THEME_BACKGROUND_COLOR,
                textColor: EVENTS_THEME_TEXT_COLOR,
                heroBackground: heroBackground?.status === 1 ? heroBackground.data : '',
                showEventWaivers: eventWaivers?.status === 1,
            },
        }));
        setLoading(false);
    };

    useEffect(() => {
        document.documentElement.style.setProperty('--background-color', settings.backgroundColor);
        document.documentElement.style.setProperty('--text-color', settings.textColor);
    }, [settings.backgroundColor, settings.textColor]);

    return (
        <SettingsContext.Provider value={{ settings, loading, saveSettings }}>{children}</SettingsContext.Provider>
    );
}
